import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { ProgramField } from '../components/programField';
import { QualificationLegend } from '../components/qualificationLegend';
import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import api from 'api';
import useEnabledCountries from 'hooks/useEnabledCountries';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadApplications } from 'redux/applications/applications-middleware';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { CaseProgramFormInterface } from 'types';
import { chosenCountryName } from 'utils/countries';
import { generateOptionsFromString } from 'utils/dataFormatter';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { caseProgramValidationSchema } from 'utils/validations/applicationRetainers';

interface CreateCaseProps {
    open?: boolean;
    onClose?: () => void;
    /** Legacy modal close used by sales onboarding and quick actions. */
    closeModal?: () => void;
    candidateId?: string | number;
    onCreated?: (applicationId: number) => void;
}

const SectionLabel: React.FC<{ children: string }> = ({ children }) => (
    <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {children}
    </Typography>
);

const CreateCaseFormDialog: React.FC<CreateCaseProps & { open: boolean }> = ({
    open,
    onClose,
    closeModal,
    candidateId: candidateIdProp,
    onCreated,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id: paramId } = useParams();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const consultantId = useSelector((state: RootState) => state.user.entity.id);
    const { codes, names: enabledCountryNames } = useEnabledCountries();
    const countryOptions = useMemo(
        () => generateOptionsFromString(enabledCountryNames),
        [enabledCountryNames]
    );
    const initialValues: CaseProgramFormInterface = useMemo(
        () => ({
            country: chosenCountryName(codes) ?? '',
            programName: '',
        }),
        [codes]
    );
    const requestClose = onClose ?? closeModal ?? (() => undefined);
    const [wasOpen, setWasOpen] = useState(open);
    const [formSession, setFormSession] = useState(0);

    if (open !== wasOpen) {
        setWasOpen(open);
        if (open) {
            setFormSession((current) => current + 1);
        }
    }

    const candidateId =
        candidateIdProp != null && String(candidateIdProp).trim() !== ''
            ? String(candidateIdProp)
            : paramId;

    const handleSubmit = async (values: CaseProgramFormInterface) => {
        if (!candidateId) {
            dispatch(
                setAlert({
                    message: translate('somethingWentWrong'),
                    type: 'error',
                })
            );
            return;
        }

        try {
            const response = await api.application.post({
                candidate_id: Number(candidateId),
                consultant_id: consultantId,
                country: values.country,
                program_name: values.programName,
            });
            const createdId = response.data?.id;
            if (!createdId) {
                dispatch(
                    setAlert({
                        message: translate('somethingWentWrong'),
                        type: 'error',
                    })
                );
                return;
            }

            await dispatch(loadApplications(candidateId));
            dispatch(setAlert({ message: translate('caseCreated'), type: 'success' }));
            onCreated?.(createdId);
            // Callers that handle the new case themselves stay on the current screen.
            if (!onCreated) {
                navigate(
                    `/dashboard/sales-onboarding/agreement/${candidateId}/visa-program/${createdId}`
                );
            }
            requestClose();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    return (
        <Formik
            key={`${formSession}:${initialValues.country}`}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={caseProgramValidationSchema}
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, setFieldTouched, setFieldValue, values }) => (
                <Dialog
                    open={open}
                    fullWidth
                    maxWidth="sm"
                    fullScreen={isMobile}
                    scroll="paper"
                    slotProps={{ paper: { sx: { display: 'flex', flexDirection: 'column' } } }}
                >
                    <Form
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <DialogTitle>{translate('caseProperties')}</DialogTitle>

                        <DialogContent
                            dividers
                            sx={{
                                flex: '1 1 auto',
                                overflowY: 'auto',
                                pt: { xs: 2, sm: 2.5 },
                            }}
                        >
                            <Stack spacing={2}>
                                <FormField
                                    required
                                    type="select"
                                    size="small"
                                    name="country"
                                    label="country"
                                    options={countryOptions}
                                    disabled={isSubmitting}
                                    onChange={() =>
                                        setFieldValue('programName', '', false).then(() =>
                                            setFieldTouched('programName', false, false)
                                        )
                                    }
                                />

                                <Box>
                                    <SectionLabel>{translate('service')}</SectionLabel>
                                    <QualificationLegend />
                                    <ProgramField
                                        userId={candidateId}
                                        active={open}
                                        disabled={isSubmitting}
                                    />
                                </Box>
                            </Stack>
                        </DialogContent>

                        <DialogActions
                            sx={{
                                flexShrink: 0,
                                px: { xs: 2, sm: 3 },
                                py: 1.5,
                                gap: 1,
                                flexDirection: 'row',
                                '& > :not(style)': {
                                    width: { xs: '100%', sm: 'auto' },
                                    m: '0 !important',
                                },
                            }}
                        >
                            <Button
                                variant="outlined"
                                onClick={requestClose}
                                disabled={isSubmitting}
                                sx={{ textTransform: 'none' }}
                            >
                                {translate('cancel')}
                            </Button>
                            <SubmitButton
                                loading={isSubmitting}
                                disabled={!values.country || !values.programName}
                                sx={{ textTransform: 'none' }}
                            >
                                saveProperties
                            </SubmitButton>
                        </DialogActions>
                    </Form>
                </Dialog>
            )}
        </Formik>
    );
};

const CreateCase: React.FC<CreateCaseProps> = (props) => (
    <ThemeProvider theme={appThemeV2}>
        <CreateCaseFormDialog {...props} open={props.open ?? true} />
    </ThemeProvider>
);

export default CreateCase;
